import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

import ElementUI from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'

Vue.use(ElementUI)

Vue.config.productionTip = false

// 辅助函数：确保错误对象有正确的 message 属性和 toString 方法
function ensureErrorMessage(err) {
  if (!err) return 'Unknown error'
  
  // 如果 err 不是 Error 实例，尝试转换
  if (!(err instanceof Error)) {
    if (typeof err === 'string') {
      return err
    } else if (typeof err === 'object') {
      // 尝试从对象中提取错误信息
      const msg = err.msg || err.message || err.error || err.reason
      if (msg && typeof msg === 'string') {
        return msg
      }
      // 如果无法提取，尝试序列化
      try {
        return JSON.stringify(err)
      } catch (e) {
        return 'Error object cannot be serialized'
      }
    }
    return String(err)
  }
  
  // 如果 message 是 [object Object] 或无效，尝试修复
  let needsFix = !err.message || err.message === '[object Object]' || err.message === '[object Error]'
  
  if (needsFix) {
    let newMessage = 'Vue component error'
    
    // 尝试从错误对象中提取信息
    if (err.response?.data?.msg) {
      newMessage = `API Error: ${err.response.data.msg}`
    } else if (err.response?.data?.message) {
      newMessage = `API Error: ${err.response.data.message}`
    } else if (err.msg) {
      newMessage = err.msg
    } else if (err.data?.msg) {
      newMessage = err.data.msg
    } else {
      // 尝试序列化错误对象的关键属性
      try {
        const keys = Object.keys(err).filter(k => k !== 'message' && k !== 'stack' && k !== 'name' && k !== 'toString')
        if (keys.length > 0) {
          const errorDetails = {}
          keys.forEach(key => {
            try {
              const value = err[key]
              if (typeof value !== 'object' || value === null) {
                errorDetails[key] = value
              } else if (Array.isArray(value)) {
                errorDetails[key] = `[Array(${value.length})]`
              } else {
                errorDetails[key] = `[Object]`
              }
            } catch (e) {
              // 忽略无法序列化的属性
            }
          })
          if (Object.keys(errorDetails).length > 0) {
            newMessage = JSON.stringify(errorDetails, null, 2)
          }
        }
      } catch (e) {
        // 如果序列化失败，使用默认消息
      }
    }
    
    // 更新错误对象的 message
    try {
      Object.defineProperty(err, 'message', {
        value: newMessage,
        writable: true,
        configurable: true
      })
      
      // 确保 toString 方法返回正确的消息
      Object.defineProperty(err, 'toString', {
        value: function() {
          return this.message || 'Error'
        },
        writable: true,
        configurable: true
      })
    } catch (e) {
      // 如果无法设置 message，至少记录到控制台
      console.error('Cannot set error message:', e)
    }
    
    return newMessage
  }
  
  // 即使 message 有效，也确保 toString 方法正确
  try {
    if (typeof err.toString !== 'function' || err.toString() === '[object Object]') {
      Object.defineProperty(err, 'toString', {
        value: function() {
          return this.message || 'Error'
        },
        writable: true,
        configurable: true
      })
    }
  } catch (e) {
    // 忽略
  }
  
  return err.message
}

// 全局错误处理
Vue.config.errorHandler = (err, vm, info) => {
  // 确保错误对象有正确的 message
  const errorMessage = ensureErrorMessage(err)
  
  // 格式化错误信息以便更好地显示
  const errorInfo = {
    message: errorMessage,
    stack: err?.stack,
    name: err?.name,
    component: vm?.$options?.name || vm?.$options?.__file,
    info: info
  }
  console.error('Vue Error:', errorInfo)
  console.error('Full Error Object:', err)
  // 可以在这里添加错误上报逻辑
}

// 全局警告处理
Vue.config.warnHandler = (msg, vm, trace) => {
  console.warn('Vue Warning:', msg)
  console.warn('Component:', vm?.$options?.name || vm?.$options?.__file)
  console.warn('Trace:', trace)
}

// 捕获未处理的 Promise 错误
window.addEventListener('unhandledrejection', event => {
  const reason = event.reason
  let errorMessage = 'Unhandled Promise Rejection'
  
  // 确保错误信息被正确提取
  if (reason instanceof Error) {
    errorMessage = ensureErrorMessage(reason)
  } else if (reason && typeof reason === 'object') {
    try {
      errorMessage = reason.msg || reason.message || reason.error || JSON.stringify(reason)
    } catch (e) {
      errorMessage = String(reason)
    }
  } else if (reason) {
    errorMessage = String(reason)
  }
  
  const errorInfo = {
    message: errorMessage,
    stack: reason?.stack,
    name: reason?.name,
    reason: reason
  }
  console.error('Unhandled Promise Rejection:', errorInfo)
  
  // 注意：event.reason 是只读的，不能直接赋值
  // 但我们可以通过创建一个新的 Error 对象来替换整个 event
  // 不过为了不影响 webpack-dev-server，我们只记录错误
  
  // 不要阻止默认处理，让 webpack-dev-server 也能捕获
  // event.preventDefault()
})

// 捕获全局错误
window.addEventListener('error', event => {
  // 确保错误信息被正确提取
  let errorMessage = event.message || 'Unknown error'
  
  if (event.error) {
    errorMessage = ensureErrorMessage(event.error)
  } else if (event.message === '[object Object]' || !event.message) {
    // 如果 event.message 本身是无效的，尝试从其他来源获取
    errorMessage = 'Global error occurred'
  }
  
  const errorInfo = {
    message: errorMessage,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error
  }
  console.error('Global Error:', errorInfo)
  
  if (event.error) {
    console.error('Error Details:', {
      message: event.error.message || errorMessage,
      stack: event.error.stack,
      name: event.error.name
    })
  }
})

new Vue({
  router,
  store,
  render: h => h(App)
}).$mount('#app')
